/* eslint-disable react/no-array-index-key */
import {
    Button,
    Divider,
    Flex,
    NumberInput,
    Select,
    SelectItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    TextInput,
    Title,
} from '@tremor/react'
import { ReactNode, useEffect, useRef, useState } from 'react'
import {
    ArrowPathIcon,
    ChevronDownIcon,
    PlayIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import {
    useApiV1KeypairsList,
    useApiV1VmCreateCreate,
    useApiV1VmDelete,
    useApiV1VmOsList,
    useApiV1VmsList,
    useApiV1VmStartCreate,
} from '../../api/api.gen'
import { EntitiesImage, ModelVMType } from '../../api/api'
import Modal from '../../components/Layout/modal'
import { toErrorString, toErrorText } from '../../utilities/error'
import Confirm from '../../components/Layout/confirm'
import Spinner from '../../components/Spinner'

function highlightWordsByQuery(
    description: string,
    queryWords: string[]
): React.ReactNode {
    const qwid = queryWords
        .map((v) => {
            return {
                qw: v,
                idx: description.toLowerCase().indexOf(v),
            }
        })
        .filter((v) => v.idx >= 0)
        .sort((a, b) => a.idx - b.idx)
        .at(0)

    if (qwid === undefined) {
        return description
    }

    return (
        <>
            {description.substring(0, qwid.idx)}
            <span className="bg-yellow-200 border">
                {description.substring(qwid.idx, qwid.idx + qwid.qw.length)}
            </span>
            {highlightWordsByQuery(
                description.substring(qwid.idx + qwid.qw.length),
                queryWords
            )}
        </>
    )
}

function highlightWords(
    description: string,
    searchQuery: string
): React.ReactNode {
    // Split the search query into words and create a Set for quick lookup
    const queryWords = searchQuery
        .toLowerCase()
        .split(' ')
        .filter((word) => word.trim().length > 0)

    return highlightWordsByQuery(description, queryWords)
}

export interface Image {
    descriptionNode?: ReactNode
    description?: string
    id?: string
    name?: string
}

interface IOSButton {
    children: any
    ami: string
    onSet: (ami: string) => void
    setDesc: (desc: string) => void
}
function OSButton({ children, ami, onSet, setDesc }: IOSButton) {
    return (
        <Flex
            className="p-1 border border-dashed border-gray-300 bg-gray-100 cursor-pointer"
            onClick={() => {
                onSet(ami)
                setDesc(String(children))
            }}
        >
            <Text className=" text-black">{children}</Text>
        </Flex>
    )
}

export default function VMPage() {
    const [deleteID, setDeleteID] = useState<number>()
    const [showModal, setShowModal] = useState(false)
    const [vmName, setVMName] = useState('')
    const [keyPairName, setKeyPairName] = useState('')
    const [ami, setAMI] = useState<string>('')
    const [vmType, setVMType] = useState<ModelVMType>(
        ModelVMType.VMTypeBurstable2Core4GB
    )
    const [volumeSize, setVolumeSize] = useState(8)
    const [osFilter, setOSFilter] = useState('')
    const [apiOSFilter, setAPIOSFilter] = useState('')
    const [filterOpen, setFilterOpen] = useState(false)
    const [images, setImages] = useState<Image[]>([])
    const filterContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        console.log(ami)
    }, [ami])

    const {
        isLoading: createLoading,
        isExecuted: createExecuted,
        response: createResponse,
        sendNowWithParams: create,
        error: createError,
    } = useApiV1VmCreateCreate({}, {}, {}, false)

    const {
        isLoading: deleteLoading,
        isExecuted: deleteExecuted,
        sendNowWithParams: deleteVM,
        error: deleteError,
    } = useApiV1VmDelete({ id: 0 }, {}, false)

    const {
        isLoading: startLoading,
        isExecuted: startExecuted,
        sendNowWithParams: startVM,
        error: startError,
    } = useApiV1VmStartCreate({ id: 0 }, {}, {}, false)

    const {
        isLoading: vmListLoading,
        isExecuted: vmListExecuted,
        response: vmList,
        sendNow: refresh,
    } = useApiV1VmsList()

    const { response: keypairList } = useApiV1KeypairsList()
    const { isLoading: osListLoading, response: OSList } = useApiV1VmOsList({
        filter: apiOSFilter,
    })

    useEffect(() => {
        setImages(
            OSList?.items?.map((v) => {
                return {
                    id: v.id,
                    name: v.name,
                    description: v.description,
                    descriptionNode: highlightWords(
                        v.description || '',
                        osFilter
                    ),
                }
            }) || []
        )
    }, [OSList])

    useEffect(() => {
        const t = setTimeout(() => setAPIOSFilter(osFilter), 500)
        return () => clearTimeout(t)
    }, [osFilter])

    useEffect(() => {
        if (
            createExecuted &&
            !createLoading &&
            toErrorString(createError).length === 0
        ) {
            setShowModal(false)
            refresh()
        }
    }, [createLoading])

    const handleClickOutside = (event: MouseEvent) => {
        if (
            filterContainerRef.current &&
            !filterContainerRef.current.contains(event.target as Node)
        ) {
            setFilterOpen(false)
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        if (
            deleteExecuted &&
            !deleteLoading &&
            toErrorString(deleteError).length === 0
        ) {
            setDeleteID(undefined)
            refresh()
        }
    }, [deleteLoading])

    const shouldContactUs =
        vmType === ModelVMType.VMTypeGraphicOptimized8Core32GB

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between">
                <Title className="text-teal-600">Virtual Machines</Title>
                <Flex className="w-fit space-x-2">
                    <Button
                        icon={ArrowPathIcon}
                        variant="secondary"
                        color="teal"
                        disabled={vmListExecuted && vmListLoading}
                        onClick={() => refresh()}
                    />
                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => setShowModal(true)}
                    >
                        Create
                    </Button>
                </Flex>
            </Flex>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>OS</TableHeaderCell>
                        <TableHeaderCell>Type</TableHeaderCell>
                        <TableHeaderCell>Volume Size</TableHeaderCell>
                        <TableHeaderCell>SSHKey</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>IP Address</TableHeaderCell>
                        <TableHeaderCell>SSH Command</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vmList?.items
                        ?.sort((a, b) => (b.id || 0) - (a.id || 0))
                        .map((v) => (
                            <TableRow>
                                <TableCell>{v.name}</TableCell>
                                <TableCell>{v.os}</TableCell>
                                <TableCell>{v.vmType}</TableCell>
                                <TableCell>{v.volumeSize}</TableCell>
                                <TableCell>{v.keyPairName}</TableCell>
                                <TableCell>{v.status}</TableCell>
                                <TableCell>{v.ipAddress}</TableCell>
                                <TableCell>
                                    {v.status !== 'deleted' && (
                                        <>ssh ubuntu@{v.ipAddress}</>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {v.status === 'stopped' && (
                                        <PlayIcon
                                            onClick={() =>
                                                startVM(
                                                    { id: v.id || 0 },
                                                    {},
                                                    {}
                                                )
                                            }
                                            className="w-4 h-4 text-teal-500 cursor-pointer"
                                        />
                                    )}
                                    {v.status !== 'deleted' && (
                                        <TrashIcon
                                            onClick={() => setDeleteID(v.id)}
                                            className="w-4 h-4 text-teal-500 cursor-pointer"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    {(vmList?.items?.length || 0) === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {vmListExecuted && vmListLoading
                                        ? 'Loading'
                                        : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Confirm
                title={`Are you sure you want to delete ${vmList?.items
                    ?.filter((v) => v.id === deleteID)
                    .map((v) => v.name)}?`}
                show={deleteID !== undefined}
                loading={deleteExecuted && deleteLoading}
                error={deleteError}
                onCanceled={() => setDeleteID(undefined)}
                onConfirmed={() => deleteVM({ id: deleteID || 0 }, {})}
            />

            <Modal
                title="Create a new Virtual Machine"
                show={showModal}
                onCloseClicked={() => setShowModal(false)}
                className="min-w-[700px]"
            >
                <Text className="mt-2">Name:</Text>
                <TextInput
                    value={vmName}
                    onValueChange={setVMName}
                    error={vmName.length === 0}
                    errorMessage="Name is required"
                />
                <Divider />

                <Text className="mb-1">SSH Key:</Text>
                <Select
                    value={keyPairName}
                    onValueChange={setKeyPairName}
                    error={keyPairName.length === 0}
                    errorMessage="SSH Key is required"
                >
                    {keypairList?.items?.map((k) => {
                        return (
                            <SelectItem value={k.name || ''}>
                                {k.name}
                            </SelectItem>
                        )
                    })}
                </Select>
                <Flex flexDirection="row" justifyContent="end">
                    <Link
                        to="/sshkey#import"
                        className="underline text-blue-600"
                    >
                        Import a ssh key
                    </Link>
                </Flex>
                <Divider />

                <Text className="mb-1">Operating System:</Text>
                <div className="grid grid-cols-3 gap-1 w-full my-2">
                    <OSButton
                        ami="ami-0bb1abbb9dd8f7bd3"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Ubuntu 24.04
                    </OSButton>
                    <OSButton
                        ami="ami-040bb8f109ed25a06"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Ubuntu 22.04
                    </OSButton>
                    <OSButton
                        ami="ami-055afb1eab2e99450"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Ubuntu 20.04
                    </OSButton>
                    <OSButton
                        ami="ami-0f448a87ba43cf7ba"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Ubuntu 18.04
                    </OSButton>
                    <OSButton
                        ami="ami-004b5d36e13fff133"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2025 Base
                    </OSButton>
                    <OSButton
                        ami="ami-060c65c2e901f8c4d"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2025 Core Base
                    </OSButton>
                    <OSButton
                        ami="ami-0a387a9bbf5210260"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2022 Base
                    </OSButton>
                    <OSButton
                        ami="ami-0061dcad0d3be8039"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2022 Core Base
                    </OSButton>
                    <OSButton
                        ami="ami-04e46df40704998b6"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2019 Base
                    </OSButton>
                    <OSButton
                        ami="ami-01cb7c7439505909a4"
                        onSet={setAMI}
                        setDesc={setOSFilter}
                    >
                        Windows Server 2019 Core Base
                    </OSButton>
                </div>
                <Flex
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    className="space-x-4 px-10"
                >
                    <div className="border-t border-gray-300 w-24" />
                    <Text>or</Text>
                    <div className="border-t border-gray-300 w-24" />
                </Flex>
                <Text>Search for image</Text>
                <Flex flexDirection="row">
                    <TextInput
                        value={osFilter}
                        onValueChange={setOSFilter}
                        onFocus={() => setFilterOpen(true)}
                        icon={osListLoading ? Spinner : undefined}
                    />
                    <ChevronDownIcon className="absolute right-11 text-gray-700 w-4" />
                </Flex>
                {filterOpen && (
                    <Flex
                        ref={filterContainerRef}
                        id="filterContainer"
                        className="relative"
                    >
                        <Flex
                            flexDirection="col"
                            className="absolute bg-white top-0 z-50 h-fit max-h-96 overflow-y-scroll"
                        >
                            {images?.map((i) => (
                                <Flex
                                    flexDirection="col"
                                    justifyContent="start"
                                    alignItems="start"
                                    className="cursor-pointer border p-1"
                                    onClick={() => {
                                        setOSFilter(() => i.description || '')
                                        setAMI(() => i.id || '')
                                        setFilterOpen(false)
                                    }}
                                >
                                    <Text className="text-black">
                                        {i.descriptionNode}
                                    </Text>
                                    <Text className="text-gray-700 text-xs">
                                        {i.name}
                                    </Text>
                                </Flex>
                            ))}
                        </Flex>
                    </Flex>
                )}
                <Divider />

                <Text className="mb-1">Type:</Text>
                <Select
                    value={vmType}
                    onValueChange={(v) => {
                        setVMType(v as ModelVMType)
                    }}
                    error={vmType.length === 0}
                    errorMessage="Type is required"
                >
                    <SelectItem value={ModelVMType.VMTypeBurstable2Core4GB}>
                        2cores, 4GB
                    </SelectItem>
                    <SelectItem value={ModelVMType.VMTypeBurstable4Core16GB}>
                        4cores, 16GB
                    </SelectItem>
                    <SelectItem
                        value={ModelVMType.VMTypeComputeOptimized8Core16GB}
                    >
                        8cores, 16GB
                    </SelectItem>
                    <SelectItem
                        value={ModelVMType.VMTypeMemoryOptimized8Core32GB}
                    >
                        8cores, 32GB
                    </SelectItem>
                    <SelectItem
                        value={ModelVMType.VMTypeMemoryIntensive8Core64GB}
                    >
                        8cores, 64GB
                    </SelectItem>
                    <SelectItem
                        value={ModelVMType.VMTypeGraphicOptimized8Core32GB}
                    >
                        8cores, 32GB + GPU (NVIDIA T4 16GB)
                    </SelectItem>
                </Select>

                <Divider />
                <Text className="mb-1">Volume Size (GB):</Text>
                <NumberInput
                    min={8}
                    max={4096}
                    value={volumeSize}
                    onValueChange={setVolumeSize}
                    error={volumeSize < 8 || volumeSize > 4096}
                    errorMessage="Volume size must be within 8-4096 range"
                />

                <div className="mt-8">{toErrorText(createError)}</div>
                {shouldContactUs && (
                    <Text className="text-red-600">
                        GPU VMs are not available on demand, please contact us.
                    </Text>
                )}
                <Button
                    className=""
                    color="teal"
                    loading={createLoading && createExecuted}
                    disabled={
                        (createLoading && createExecuted) || shouldContactUs
                    }
                    onClick={() => {
                        create(
                            {
                                name: vmName,
                                keyPairName,
                                osami: ami,
                                vmType,
                                volumeSize,
                            },
                            {},
                            {}
                        )
                    }}
                >
                    Create
                </Button>
            </Modal>
        </Flex>
    )
}
